import { Emitter } from 'mitt';
import { ChatFrameworkDynamicOptions, ChatFrameworkEvents, InitialConnectAiEvent } from '../ChatFramework.types';
import { MessageStoreApi } from '../messages';
import { buildConnectConfig, canConnectToAgent, EventType, mergeConnectConfig } from '../../lib';
import { BaseEvent, ConnectToAgent } from '../../types';
import { AI_EVENTS, analytics } from '@/common/logs';
import { reportingService } from '@/common/reporting';
import { AiThresholdLimitError, isAiThresholdLimitError } from '@/common/error';
import * as PleasedApi from '@/common/api/api.pleased';
import { AiResponseAction } from '@/common/api/models.pleased';

// Define an interface for the methods the AI service needs.
export interface ChatFrameworkInterface {
  emitter: Emitter<ChatFrameworkEvents>;
  messageList: MessageStoreApi;
  add: (message: any | Promise<any>) => void;
  getOptions: () => ChatFrameworkDynamicOptions;
  setOptions: (options: Partial<ChatFrameworkDynamicOptions>) => void;
  submit: (event: BaseEvent) => void;
  connectToAgentFromConfig: (connectConfig: ConnectToAgent) => void;
}

type GetAiResponseParams = {
  message: string;
  event: InitialConnectAiEvent;
  isFirstRequest: boolean;
  welcomeMessage: string;
};

export class AiService {
  private chatFramework: ChatFrameworkInterface

  constructor (chatFramework: ChatFrameworkInterface) {
    this.chatFramework = chatFramework;
  }

  // AI TODO: refactor this function to not include logic of controlling the flow.
  public async getAiResponse (params: GetAiResponseParams) {
    const { message, event, isFirstRequest, welcomeMessage } = params;
    const options = this.chatFramework.getOptions();

    const isExternalEvent = EventType.isExternalEvent(event);
    const connectConfig = (isExternalEvent
      ? event.payload.supportOptions
      : (event as BaseEvent).message.config?.connect) as ConnectToAgent;

    const ableToConnectToAgent = canConnectToAgent(connectConfig);
    const hardcodedAllowConnectConfig = buildConnectConfig({
      connectToAgent: true, loginRequired: true, showLiveChat: true, showVoiceChat: true, showOpenRequest: true,
    });

    // Here we create connect config for finish flow based on the ability to connect to agent.
    // If we can connect to agent, we'll show existing options, otherwise we'll all hardcoded options.
    const finishFlowConnectConfig = ableToConnectToAgent
      ? connectConfig
      : mergeConnectConfig(connectConfig, hardcodedAllowConnectConfig);

    try {
      const params = {
        user_id: options.userId ? `${options.userId}` : '',
        user_input: message,
        session_id: analytics.sessionId || '',
        first_request: isFirstRequest,
        ...(isFirstRequest ? { userLogs: reportingService.getLogs(), welcomeMessage } : {}),
      };

      analytics.trackAiEvent({
        name: AI_EVENTS.AI_USER_REQUEST,
        metadata: { ...params },
      });

      const aiResponse = await PleasedApi.getAiChatResponse(params);

      // TODO: Update to 'switcher' when more actions are added.
      if (aiResponse.action === AiResponseAction.CONNECT_TO_AGENT) {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_CONNECT_SUPPORT_RESPONSE,
          metadata: { message, aiResponse },
        });

        this.connectToAgentFromAiFlow(finishFlowConnectConfig);
      }

      if (aiResponse.action === AiResponseAction.END_CHAT) {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_END_CHAT_RESPONSE,
          metadata: { message, aiResponse },
        });

        this.chatFramework.setOptions({ aiEnabledPerSession: false });
        this.chatFramework.emitter.emit('endAiConversation');
      }

      return aiResponse;
    } catch (e) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_ERROR_RESPONSE,
        metadata: { message, error: e },
      });
      console.error('Error in AI flow', e);

      const resumeDefaultChatFlow =
          (!isExternalEvent && isAiThresholdLimitError(e as Error)) || isFirstRequest;

      if (resumeDefaultChatFlow) {
        this.chatFramework.setOptions({ aiEnabledPerSession: false });
        this.chatFramework.emitter.emit('resumeChatFlow');
        this.chatFramework.submit(event as BaseEvent);
        throw new AiThresholdLimitError();
      }

      this.connectToAgentFromAiFlow(finishFlowConnectConfig);
      throw e;
    }
  }

  protected connectToAgentFromAiFlow (connectConfig: ConnectToAgent) {
    this.chatFramework.setOptions({ aiEnabledPerSession: false });
    this.chatFramework.connectToAgentFromConfig(connectConfig);
  }
}
